import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { filterParam } from '@/lib/url-state';
import { configQuery, configsListQuery, configsSearchSchema } from './api';
import { ConfigNotFound } from './pages/config-not-found';

/** The scope of a resolution (explorer) or of a new row (create, prefilled from the explorer). */
export const scopeSearchSchema = z.object({
  operator: filterParam.number(),
  brand: filterParam.string(),
  game: filterParam.number(),
  currency: filterParam.string(),
});

export type ScopeSearch = z.infer<typeof scopeSearchSchema>;

/** Route param → config id; anything that isn't a positive integer is a 404. */
export function parseConfigId(raw: string): number {
  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  return id;
}

async function loadConfig(context: RouterContext, raw: string) {
  const id = parseConfigId(raw);
  try {
    await context.queryClient.ensureQueryData(configQuery(id));
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

export const configsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'configs',
  validateSearch: configsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(configsListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/configs-list-page'), 'ConfigsListPage'),
});

export const effectiveConfigRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'configs/effective',
  validateSearch: scopeSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  component: lazyRouteComponent(() => import('./pages/effective-config-page'), 'EffectiveConfigPage'),
});

export const configCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'configs/new',
  validateSearch: scopeSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES', 'UPDATE_GAMES'),
  staticData: { crumbs: () => [{ label: 'New config' }] },
  component: lazyRouteComponent(() => import('./pages/config-create-page'), 'ConfigCreatePage'),
});

export const configDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'configs/$configId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES'),
  loader: ({ context, params }) => loadConfig(context, params.configId),
  staticData: { crumbs: (params) => [{ label: `#${params.configId ?? ''}` }] },
  notFoundComponent: ConfigNotFound,
  component: lazyRouteComponent(() => import('./pages/config-detail-page'), 'ConfigDetailPage'),
});

export const configEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'configs/$configId/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES', 'UPDATE_GAMES'),
  loader: ({ context, params }) => loadConfig(context, params.configId),
  staticData: {
    crumbs: (params) => [
      { label: `#${params.configId ?? ''}`, href: `/configs/${params.configId ?? ''}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: ConfigNotFound,
  component: lazyRouteComponent(() => import('./pages/config-edit-page'), 'ConfigEditPage'),
});

/** Routes for this feature, attached to `appRoute`; `app/routeTree.ts` spreads them in. */
export const configsRoutes = [
  configsListRoute,
  effectiveConfigRoute,
  configCreateRoute,
  configDetailRoute,
  configEditRoute,
] as const;
